import { useEffect, useState, type ReactNode } from 'react';
import {
  DEAL_SOURCES,
  FORECAST_CATEGORIES,
  contactSchema,
  toWireDate,
  type Contact,
} from '@forecastify/shared';
import { Button, Icon, MultiSelect, TextInput, type MenuItem } from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { useDuration } from '../../duration';
import { useSession } from '../../session';
import {
  RailCard,
  RailDate,
  RailMoney,
  RailPicker,
  RailRow,
  RailValue,
} from '../contracts/detail-chrome';
import { formatCalendarDate } from '../history/history-labels';
import { parseList } from '../permissions-hook';
import { FORECAST_COLORS, dealMoney, forecastLabel, sourceLabel } from './deal-kit';
import type { DealTabProps } from './deal-tab';
import { PersonAvatar, RailText, Refusal, useCardWrite } from './overview-parts';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D5 · the overview's right rail.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *Érték · Esély · Erőforrás · Sortételek · Résztvevők · Részletek*, each a card with its own
 * refusal slot. Every PATCH carries the record's `version`; the answer is the whole record, so a
 * changed probability moves the weighted value and the forecast dot without a second request.
 *
 * ⚠️ The rail controls are the contract rail's (`detail-chrome.tsx`) — `RailPicker` rather than
 * `Select` because the forecast row carries a dot and the owner row a face, which a listbox option
 * cannot draw. Read-only (`!canManage`) draws the same rows as plain values.
 */

type RailProps = Pick<DealTabProps, 'deal' | 'record' | 'feeds' | 'canManage' | 'onTab'>;

/** `"21000000.00"` → `21000000`, what the money box shows. */
function amountText(value: string): string {
  const amount = Number(value);
  return Number.isFinite(amount) ? String(amount) : '';
}

export function OverviewRail(props: RailProps) {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <ValueCard {...props} />
      <ChanceCard {...props} />
      <ResourceCard {...props} />
      <LinesCard {...props} />
      <PeopleCard {...props} />
      <DetailsCard {...props} />
    </div>
  );
}

/** A rail card whose title strip carries a link to another tab — *Teljes terv →*. */
function SummaryCard({
  title,
  link,
  onLink,
  children,
}: {
  readonly title: string;
  readonly link: string;
  readonly onLink: () => void;
  readonly children: ReactNode;
}) {
  return (
    <div className="bg-card flex flex-col gap-2 rounded-xl border p-3 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {title}
        </div>
        <div className="flex-1" />
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-[11px] transition-colors"
          onClick={onLink}
        >
          {link}
          <Icon name="arrow-right" size={11} />
        </button>
      </div>
      {children}
    </div>
  );
}

/** One `label … figure` line of a summary card, dashed between rows as the mockup rules them. */
function SummaryRow({
  label,
  value,
  strong = false,
  dashed = true,
  className = '',
}: {
  readonly label: string;
  readonly value: string;
  readonly strong?: boolean;
  readonly dashed?: boolean;
  readonly className?: string;
}) {
  return (
    <div
      className={`flex items-baseline gap-2 py-1 ${dashed ? 'border-b border-dashed last:border-0' : ''}`}
    >
      <span className="text-muted-foreground min-w-0 flex-1 truncate pl-1 text-[11.5px]">
        {label}
      </span>
      <span
        className={`shrink-0 text-[12px] tabular-nums ${strong ? 'font-medium' : ''} ${className}`}
      >
        {value}
      </span>
    </div>
  );
}

/* ── Érték ─────────────────────────────────────────────────────────────────────────────────── */

function ValueCard({ deal, record, feeds, canManage }: RailProps) {
  const t = useT();
  const write = useCardWrite(record);
  const modeText =
    deal.valueMode === 'plan'
      ? t('dealOverview.rail.mode.plan')
      : t('dealOverview.rail.mode.indicative');
  const patch = (body: Record<string, unknown>): void => {
    void write.run('', 'PATCH', { version: deal.version, ...body });
  };

  return (
    <RailCard title={t('dealOverview.rail.value')} padded>
      <div className="mt-2 flex flex-col gap-1.5">
        <div className="flex items-baseline gap-2">
          <span className="text-[20px] leading-tight font-semibold tabular-nums">
            {dealMoney(deal.value, deal.currencyCode)}
          </span>
          <span className="text-muted-foreground text-[11px]">{deal.currencyCode}</span>
        </div>
        <div className="text-muted-foreground text-[11.5px] tabular-nums">
          {t('dealOverview.rail.weighted', {
            value: dealMoney(deal.weightedValue, deal.currencyCode),
          })}
        </div>
        <RailPicker
          label={t('dealOverview.rail.mode.label')}
          value={modeText}
          disabled={!canManage}
          items={[
            {
              label: t('dealOverview.rail.mode.pickIndicative'),
              description: t('dealOverview.rail.mode.pickIndicativeHint'),
              checked: deal.valueMode === 'indicative',
              onSelect: () => {
                if (deal.valueMode !== 'indicative') patch({ valueMode: 'indicative' });
              },
            },
            {
              label: t('dealOverview.rail.mode.pickPlan'),
              description: t('dealOverview.rail.mode.pickPlanHint'),
              checked: deal.valueMode === 'plan',
              onSelect: () => {
                if (deal.valueMode !== 'plan') patch({ valueMode: 'plan' });
              },
            },
          ]}
        />
        {/* The value's currency, beside the value it labels. Relabels, never converts. */}
        <RailPicker
          label={t('dealOverview.rail.currency.label')}
          value={deal.currencyCode}
          disabled={!canManage || feeds.currencies.length < 2}
          items={feeds.currencies.map((currency) => ({
            label: `${currency.code} · ${currency.name}`,
            description: t('dealOverview.rail.currency.hint'),
            checked: currency.code === deal.currencyCode,
            onSelect: () => {
              if (currency.code !== deal.currencyCode) patch({ currencyCode: currency.code });
            },
          }))}
        />
        {deal.valueMode === 'indicative' && canManage ? (
          <RailMoney
            value={amountText(deal.indicativeValue)}
            label={t('dealOverview.rail.indicative')}
            suffix={deal.currencyCode}
            placeholder="0"
            onCommit={(value) => {
              patch({ indicativeValue: value ?? 0 });
            }}
          />
        ) : null}
        <Refusal error={write.error} />
      </div>
    </RailCard>
  );
}

/* ── Esély ─────────────────────────────────────────────────────────────────────────────────── */

function ChanceCard({ deal, record, feeds, canManage }: RailProps) {
  const t = useT();
  const { locale } = useI18n();
  const write = useCardWrite(record);
  /** A later close date waits here for its reason before it is written. */
  const [slip, setSlip] = useState<{ to: string; reason: string } | null>(null);
  const patch = async (body: Record<string, unknown>): Promise<boolean> =>
    write.run('', 'PATCH', { version: deal.version, ...body });

  const probabilities = [...(feeds.config?.probabilities ?? [])].sort((a, b) => a.pct - b.pct);
  const probabilityItems: MenuItem[] = probabilities.map((entry) => ({
    label: `${String(entry.pct)}% · ${entry.name}`,
    ...(entry.description === '' ? {} : { description: entry.description }),
    dot: entry.color,
    checked: deal.probability === entry.pct,
    onSelect: () => {
      if (deal.probability !== entry.pct) void patch({ probability: entry.pct });
    },
  }));

  const forecastItems: MenuItem[] = [
    {
      label: t('dealOverview.rail.forecastAuto'),
      description: t('dealOverview.rail.forecastAutoHint'),
      checked: !deal.forecastOverridden,
      onSelect: () => {
        if (deal.forecastOverridden) void patch({ forecastOverride: null });
      },
    },
    { type: 'separator' },
    ...FORECAST_CATEGORIES.map((category) => ({
      label: forecastLabel(t, category),
      dot: FORECAST_COLORS[category],
      checked: deal.forecastOverridden && deal.forecastCategory === category,
      onSelect: () => {
        void patch({ forecastOverride: category });
      },
    })),
  ];

  const moveClose = (value: string): void => {
    const next = value === '' ? null : value;
    if (next === deal.closeDate) return;
    /* ⚠️ Only a move LATER is a slip and asks why — an earlier date or a first date is news. */
    if (deal.closeDate !== null && next !== null && next > deal.closeDate) {
      setSlip({ to: next, reason: '' });
      return;
    }
    setSlip(null);
    void patch({ closeDate: next });
  };

  const saveSlip = async (): Promise<void> => {
    if (slip === null) return;
    const ok = await patch({
      closeDate: slip.to,
      ...(slip.reason.trim() === '' ? {} : { closeDateReason: slip.reason.trim() }),
    });
    if (ok) setSlip(null);
  };

  return (
    <RailCard title={t('dealOverview.rail.chance')} padded>
      <div className="mt-2 flex flex-col gap-1">
        <RailRow label={t('dealOverview.rail.probability')}>
          <RailPicker
            label={t('dealOverview.rail.probability')}
            value={`${String(deal.probability)}%`}
            items={probabilityItems}
            disabled={!canManage || probabilityItems.length === 0}
          />
        </RailRow>
        <RailRow label={t('dealOverview.rail.forecast')}>
          <RailPicker
            label={t('dealOverview.rail.forecast')}
            value={
              deal.forecastOverridden
                ? `${forecastLabel(t, deal.forecastCategory)} · ${t('dealOverview.rail.forecastOverridden')}`
                : forecastLabel(t, deal.forecastCategory)
            }
            before={
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ background: FORECAST_COLORS[deal.forecastCategory] }}
              />
            }
            items={forecastItems}
            disabled={!canManage}
          />
        </RailRow>
        <RailRow label={t('dealOverview.rail.close')}>
          {canManage ? (
            <RailDate
              label={t('dealOverview.rail.close')}
              value={slip?.to ?? deal.closeDate ?? ''}
              onChange={moveClose}
            />
          ) : (
            <RailValue>
              {deal.closeDate === null ? '–' : formatCalendarDate(deal.closeDate, locale)}
            </RailValue>
          )}
        </RailRow>
        {slip === null ? null : (
          <div className="bg-muted/40 mt-1 flex flex-col gap-2 rounded-lg border p-2.5">
            <span className="text-[12px] font-medium tabular-nums">
              {`${deal.closeDate === null ? '–' : formatCalendarDate(deal.closeDate, locale)} → ${formatCalendarDate(slip.to, locale)}`}
            </span>
            <span className="text-muted-foreground text-[11.5px] leading-relaxed">
              {t('dealOverview.rail.slipHint')}
            </span>
            <TextInput
              autoFocus
              value={slip.reason}
              maxLength={300}
              aria-label={t('dealOverview.rail.slipReason')}
              placeholder={t('dealOverview.rail.slipPlaceholder')}
              onChange={(event) => {
                setSlip({ ...slip, reason: event.target.value });
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void saveSlip();
                if (event.key === 'Escape') setSlip(null);
              }}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSlip(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button size="sm" disabled={write.busy} onClick={() => void saveSlip()}>
                {t('dealOverview.rail.slipSave')}
              </Button>
            </div>
          </div>
        )}
        <Refusal error={write.error} />
      </div>
    </RailCard>
  );
}

/* ── Erőforrás and Sortételek ──────────────────────────────────────────────────────────────── */

function ResourceCard({ deal, onTab }: RailProps) {
  const t = useT();
  const { fmt } = useDuration();
  const { plan } = deal;
  const negative = Number(plan.margin) < 0;
  return (
    <SummaryCard
      title={t('dealOverview.rail.resource')}
      link={t('dealOverview.rail.fullPlan')}
      onLink={() => {
        onTab('resource');
      }}
    >
      <div className="flex flex-col gap-0.5">
        <SummaryRow
          label={t('dealOverview.rail.placeholders')}
          value={String(deal.placeholders.length)}
        />
        <SummaryRow label={t('dealOverview.rail.allocated')} value={fmt(plan.allocatedHours)} />
        <SummaryRow
          label={t('dealOverview.rail.planCost')}
          value={dealMoney(plan.laborCost, deal.currencyCode)}
        />
        {/* ⚠️ No revenue, no margin — `—`, never `0%` (`state-rules.md` §5). */}
        <SummaryRow
          label={t('dealOverview.rail.planMargin')}
          value={plan.marginPct === null ? '—' : `${String(Math.round(plan.marginPct * 100))}%`}
          className={negative ? 'text-destructive' : ''}
        />
      </div>
    </SummaryCard>
  );
}

function LinesCard({ deal, onTab }: RailProps) {
  const t = useT();
  const { fmt } = useDuration();
  const { locale } = useI18n();
  const { plan, lineItems } = deal;
  const toPlan = (): void => {
    onTab('plan');
  };
  const period =
    plan.startsOn === null || plan.endsOn === null
      ? '–'
      : `${formatCalendarDate(plan.startsOn, locale)} – ${formatCalendarDate(plan.endsOn, locale)}`;

  return (
    <SummaryCard
      title={t('dealOverview.rail.lines')}
      link={t('dealOverview.rail.toPlan')}
      onLink={toPlan}
    >
      {lineItems.length === 0 ? (
        <button
          type="button"
          className="text-muted-foreground hover:bg-accent/40 rounded-md border border-dashed px-2 py-2 text-left text-[11.5px]"
          onClick={toPlan}
        >
          {t('dealOverview.rail.linesEmpty')}
        </button>
      ) : (
        <>
          <div className="flex flex-col gap-0.5">
            {lineItems.slice(0, 4).map((item) => (
              <SummaryRow
                key={item.id}
                label={item.name}
                value={dealMoney(item.amount, item.currencyCode)}
                className={item.type === 'option' ? 'text-muted-foreground' : ''}
              />
            ))}
          </div>
          {lineItems.length > 4 ? (
            <div className="text-muted-foreground pl-1 text-[11px] tabular-nums">
              {t('dealOverview.rail.linesMore', { count: lineItems.length - 4 })}
            </div>
          ) : null}
          <div className="flex flex-col gap-0.5 border-t pt-1.5">
            <SummaryRow
              label={t('dealOverview.rail.linesSum')}
              value={dealMoney(plan.revenue, deal.currencyCode)}
              strong
              dashed={false}
            />
            <SummaryRow
              label={t('dealOverview.rail.linesEstimate')}
              value={fmt(plan.estimateHours)}
              strong
              dashed={false}
            />
            <SummaryRow
              label={t('dealOverview.rail.linesPeriod')}
              value={period}
              strong
              dashed={false}
            />
          </div>
        </>
      )}
    </SummaryCard>
  );
}

/* ── Résztvevők ────────────────────────────────────────────────────────────────────────────── */

function PeopleCard({ deal, record, feeds, canManage }: RailProps) {
  const t = useT();
  const session = useSession();
  const write = useCardWrite(record);
  const [contacts, setContacts] = useState<readonly Contact[]>([]);
  const patch = (body: Record<string, unknown>): void => {
    void write.run('', 'PATCH', { version: deal.version, ...body });
  };

  /* The client's own contacts (DM-2) — only a manager can pick one, so only a manager asks. */
  useEffect(() => {
    if (!canManage) return;
    const abort = new AbortController();
    void (async () => {
      const response = await session
        .request(`/contacts?client=${encodeURIComponent(deal.clientId)}`, {
          signal: abort.signal,
        })
        .catch(() => null);
      if (response?.ok !== true || abort.signal.aborted) return;
      const parsed = parseList(contactSchema, await response.json());
      if (parsed !== null) setContacts(parsed);
    })();
    return () => {
      abort.abort();
    };
  }, [session, deal.clientId, canManage]);

  const contactLine = [
    deal.contact?.title ?? '',
    deal.contact?.email ?? '',
    deal.contact?.phone ?? '',
  ]
    .filter((part) => part !== '')
    .join(' · ');

  return (
    <RailCard title={t('dealOverview.rail.people')} padded>
      <div className="mt-2 flex flex-col gap-1">
        <RailRow label={t('dealOverview.rail.contact')}>
          <RailPicker
            label={t('dealOverview.rail.contact')}
            value={deal.contact?.name ?? t('dealOverview.rail.noContact')}
            disabled={!canManage}
            items={[
              {
                label: t('dealOverview.rail.noContact'),
                checked: deal.contactId === null,
                onSelect: () => {
                  if (deal.contactId !== null) patch({ contactId: null });
                },
              },
              ...contacts.map((contact) => ({
                label: contact.name,
                ...(contact.title === '' ? {} : { description: contact.title }),
                checked: deal.contactId === contact.id,
                onSelect: () => {
                  if (deal.contactId !== contact.id) patch({ contactId: contact.id });
                },
              })),
            ]}
          />
        </RailRow>
        {contactLine === '' ? null : (
          <div className="text-muted-foreground pl-1 text-[11px] leading-relaxed break-words">
            {contactLine}
          </div>
        )}
        {deal.otherContacts.length === 0 ? null : (
          <div className="flex flex-col gap-0.5 pl-1">
            {deal.otherContacts.map((contact) => (
              <div key={contact.id} className="text-muted-foreground text-[11.5px]">
                {contact.name}
                {contact.title === '' ? null : (
                  <span className="opacity-70">{` · ${contact.title}`}</span>
                )}
              </div>
            ))}
          </div>
        )}
        <RailRow label={t('dealOverview.rail.owner')} divided>
          <RailPicker
            label={t('dealOverview.rail.owner')}
            value={deal.ownerName}
            disabled={!canManage}
            searchPlaceholder={t('dealOverview.rail.searchPeople')}
            before={
              <PersonAvatar
                name={deal.ownerName}
                memberId={deal.ownerMemberId}
                members={feeds.members}
              />
            }
            items={feeds.members.map((member) => ({
              label: member.displayName,
              checked: deal.ownerMemberId === member.id,
              onSelect: () => {
                if (deal.ownerMemberId !== member.id) patch({ ownerMemberId: member.id });
              },
            }))}
          />
        </RailRow>
        <RailRow label={t('dealOverview.rail.followers')}>
          {canManage ? (
            <MultiSelect
              label={t('dealOverview.rail.followers')}
              placeholder={t('dealOverview.rail.noFollowers')}
              searchable
              searchPlaceholder={t('dealOverview.rail.searchPeople')}
              values={deal.followers.map((follower) => follower.memberId)}
              onValuesChange={(values) => {
                patch({ followerIds: values });
              }}
              options={feeds.members.map((member) => ({
                value: member.id,
                label: member.displayName,
              }))}
            />
          ) : deal.followers.length === 0 ? (
            <RailValue mono={false}>{t('dealOverview.rail.noFollowers')}</RailValue>
          ) : (
            <span className="flex h-[30px] items-center gap-1 px-2">
              {deal.followers.map((follower) => (
                <PersonAvatar
                  key={follower.memberId}
                  name={follower.name}
                  memberId={follower.memberId}
                  members={feeds.members}
                />
              ))}
            </span>
          )}
        </RailRow>
        <Refusal error={write.error} />
      </div>
    </RailCard>
  );
}

/* ── Részletek ─────────────────────────────────────────────────────────────────────────────── */

function DetailsCard({ deal, record, canManage }: RailProps) {
  const t = useT();
  const { locale } = useI18n();
  const write = useCardWrite(record);
  const patch = (body: Record<string, unknown>): void => {
    void write.run('', 'PATCH', { version: deal.version, ...body });
  };
  const created = [
    formatCalendarDate(toWireDate(new Date(deal.createdAt)), locale),
    deal.createdByName ?? '',
  ]
    .filter((part) => part !== '')
    .join(' · ');

  return (
    <RailCard title={t('dealOverview.rail.details')} padded>
      <div className="mt-2 flex flex-col gap-1">
        <RailRow label={t('dealOverview.rail.source')}>
          <RailPicker
            label={t('dealOverview.rail.source')}
            value={sourceLabel(t, deal.source)}
            disabled={!canManage}
            items={DEAL_SOURCES.map((source) => ({
              label: sourceLabel(t, source),
              checked: deal.source === source,
              onSelect: () => {
                if (deal.source !== source) patch({ source });
              },
            }))}
          />
        </RailRow>
        <RailRow label={t('dealOverview.rail.campaign')}>
          {canManage ? (
            <RailText
              value={deal.campaign}
              maxLength={120}
              label={t('dealOverview.rail.campaign')}
              onCommit={(campaign) => {
                patch({ campaign });
              }}
            />
          ) : (
            <RailValue mono={false}>{deal.campaign === '' ? '–' : deal.campaign}</RailValue>
          )}
        </RailRow>
        <RailRow label={t('dealOverview.rail.competitor')}>
          {canManage ? (
            <RailText
              value={deal.competitor}
              maxLength={120}
              label={t('dealOverview.rail.competitor')}
              onCommit={(competitor) => {
                patch({ competitor });
              }}
            />
          ) : (
            <RailValue mono={false}>{deal.competitor === '' ? '–' : deal.competitor}</RailValue>
          )}
        </RailRow>
        <RailRow label={t('dealOverview.rail.tags')}>
          {canManage ? (
            <RailText
              value={deal.tags.join(', ')}
              label={t('dealOverview.rail.tags')}
              placeholder={t('dealOverview.rail.tagsPlaceholder')}
              onCommit={(text) => {
                patch({
                  tags: text
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter((tag) => tag !== ''),
                });
              }}
            />
          ) : (
            <RailValue mono={false}>
              {deal.tags.length === 0 ? '–' : deal.tags.join(', ')}
            </RailValue>
          )}
        </RailRow>
        <RailRow label={t('dealOverview.rail.created')}>
          <RailValue mono={false}>{created}</RailValue>
        </RailRow>
        <Refusal error={write.error} />
      </div>
    </RailCard>
  );
}
